import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Eye, EyeOff } from "@/components/icons";
import api, { formatErr } from "../lib/api";
import { AuthCard, FlatInput, PrimaryButton } from "./Login";

/**
 * ResetPassword — estilo minimalista "Files Management".
 * Fondo blanco full-screen. Paleta blanco / gris #F2F3F5 / negro.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwd.length < 6) {
      setErr("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (pwd !== pwd2) {
      setErr("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
    } catch (e2) {
      setErr(
        formatErr(e2.response?.data?.detail) ||
          "El enlace no es válido o ya fue utilizado."
      );
    } finally {
      setLoading(false);
    }
  };

  // Missing token edge case.
  if (!token) {
    return (
      <div className="min-h-screen w-full bg-white flex items-center justify-center p-4 sm:p-6">
        <AuthCard>
          <div className="text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#F2F3F5] flex items-center justify-center">
              <XCircle className="w-6 h-6 text-black" />
            </div>
            <h1 className="mt-6 text-2xl sm:text-[28px] font-semibold text-black tracking-tight">
              Enlace inválido
            </h1>
            <p className="mt-3 text-sm text-black/60">
              Este enlace no es válido. Solicita uno nuevo desde la pantalla de
              recuperación.
            </p>
            <Link
              to="/recuperar-contrasena"
              className="mt-7 inline-block w-full bg-black hover:bg-black/90 text-white rounded-xl py-3.5 text-[15px] font-semibold transition"
            >
              Solicitar nuevo enlace
            </Link>
          </div>
        </AuthCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-white flex items-center justify-center p-4 sm:p-6">
      <AuthCard>
        <div className="flex justify-center mb-8">
          <img
            src="/brand/consudoctor-icon.png"
            alt="ConsuDoctor"
            className="h-12 w-12 object-contain"
          />
        </div>

        {!done ? (
          <div data-testid="reset-password-card">
            <h1 className="text-center text-3xl sm:text-[32px] font-semibold text-black tracking-tight leading-tight">
              Nueva contraseña
            </h1>
            <p className="text-center text-sm text-black/50 mt-2">
              Elige una contraseña segura. Mínimo 6 caracteres.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-3">
              <PasswordField
                label="Nueva contraseña"
                value={pwd}
                onChange={setPwd}
                show={show}
                onToggleShow={() => setShow((v) => !v)}
                testId="reset-new-password-input"
                toggleTestId="reset-toggle-password-visibility"
              />
              <FlatInput
                label="Confirmar contraseña"
                type={show ? "text" : "password"}
                value={pwd2}
                onChange={setPwd2}
                placeholder="Repite la contraseña"
                testId="reset-confirm-password-input"
                autoComplete="new-password"
              />

              {err && (
                <div
                  data-testid="reset-error"
                  className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5"
                >
                  {err}
                </div>
              )}

              <PrimaryButton
                type="submit"
                loading={loading}
                testId="reset-submit-btn"
              >
                Cambiar contraseña
              </PrimaryButton>
            </form>
          </div>
        ) : (
          <div className="text-center" data-testid="reset-success">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#F2F3F5] flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-black" />
            </div>
            <h1 className="mt-6 text-2xl sm:text-[28px] font-semibold text-black tracking-tight">
              ¡Contraseña actualizada!
            </h1>
            <p className="mt-3 text-sm text-black/60">
              Ya puedes iniciar sesión con tu nueva contraseña.
            </p>
            <button
              type="button"
              onClick={() => nav("/login", { replace: true })}
              data-testid="reset-go-login-btn"
              className="mt-7 w-full bg-black hover:bg-black/90 text-white rounded-xl py-3.5 text-[15px] font-semibold transition"
            >
              Iniciar sesión
            </button>
          </div>
        )}
      </AuthCard>
    </div>
  );
}

/** Password input con toggle de mostrar/ocultar en el mismo estilo flat. */
function PasswordField({ label, value, onChange, show, onToggleShow, testId, toggleTestId }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-black/50 font-semibold mb-1.5 px-1">
        {label}
      </span>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="••••••••"
          data-testid={testId}
          required
          autoComplete="new-password"
          className="w-full bg-[#F2F3F5] hover:bg-[#EDEEF0] focus:bg-white border border-transparent focus:border-black/80 rounded-xl pl-4 pr-11 py-3.5 text-black text-[15px] placeholder-black/30 outline-none transition-all"
        />
        <button
          type="button"
          onClick={onToggleShow}
          data-testid={toggleTestId}
          aria-label="Mostrar/ocultar contraseña"
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 inline-flex items-center justify-center rounded-xl text-black/50 hover:text-black hover:bg-black/[0.05] transition"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </label>
  );
}
